/**
 * Read-only display for a clinician-typed free-text clinical field (chief complaints,
 * clinical findings, diagnosis, hospital course, discharge advice, etc.) that auto-translates
 * into the current UI language via the cached LibreTranslate endpoint (utils/autoTranslate.ts
 * -> backend POST /translate/auto), instead of requiring a manual click.
 *
 * This is a deliberate, explicit exception to the earlier "never auto-translate a clinician's
 * own notes" rule from earlier in this project — the user was told machine-translating free
 * clinical text (as opposed to fixed catalog values like drug/lab names) carries real risk of
 * subtly changing clinical meaning, and chose to enable it anyway for already-SAVED/read-only
 * text. To keep that safe:
 *   - This is NEVER used on an editable field. Editing always shows/saves the clinician's
 *     original text exactly as typed, in whatever language — only a already-saved, read-only
 *     VIEW (e.g. a finalized discharge summary, a past visit's notes) uses this component.
 *   - The machine-translated text always carries a small "🤖 Machine translated" label and a
 *     "Show original" toggle, so nobody reading it can mistake it for the literal wording a
 *     clinician wrote, and can always flip back to what was actually documented.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAutoTranslate } from '../../utils/autoTranslate';

export default function AutoTranslatedNote({ text, className }: { text: string | undefined | null; className?: string }) {
  const { t, i18n } = useTranslation();
  const [showOriginal, setShowOriginal] = useState(false);
  const lang = (i18n.language || 'en').split('-')[0];
  const original = text || '';
  const translated = useAutoTranslate(original);

  if (!original) return <span className={className}>—</span>;
  if (lang === 'en' || translated === original) return <span className={className}>{original}</span>;

  return (
    <div>
      <p className={className}>{showOriginal ? original : translated}</p>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[10px] text-amber-600 font-semibold">🤖 {t('common.machineTranslated', 'Machine translated')}</span>
        <button
          type="button"
          onClick={() => setShowOriginal((s) => !s)}
          className="text-[10px] text-[#02357A] hover:underline font-semibold"
        >
          {showOriginal ? t('common.showTranslated', 'Show translated') : t('common.showOriginal', 'Show original')}
        </button>
      </div>
    </div>
  );
}
